import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { useEffect, useState, useRef } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { signupService } from "@/services/signup"
import { useAuth } from "@/contexts/auth-context"

type State = 'pending' | 'success' | 'error'

export default function ConfirmationPage() {
  const navigate = useNavigate()
  const {} = useAuth()
  const { token } = useParams()
  const [state, setState] = useState<State>('pending')
  const [message, setMessage] = useState<string>('')
  const hasConfirmed = useRef(false)

  useEffect(() => {
    // Prevent multiple confirmations
    if (hasConfirmed.current) {
      return
    }

    if (!token) {
      setState('error')
      setMessage('Invalid confirmation link. Please check your email.')
      return
    }

    const handleConfirm = async () => {
      // Mark as confirmed to prevent re-runs
      hasConfirmed.current = true

      try {
        const response = await signupService.newConfirmation(token)
        if (response?.token) {
          localStorage.setItem('authToken', response.token)
          localStorage.setItem('reset_password_token', response.reset_password_token)
          
          // Immediately redirect to password setup - no waiting
          navigate('/get-started/profile')
        }
      } catch (err: any) {
        setState('error')
        if (err?.response?.data?.email) {
          setMessage(`Email ${err.response.data.email[0]}`)
        } else if (err?.response?.data?.confirmation_token) {
          setMessage(`Confirmation Token ${err.response.data.confirmation_token[0]}`)
        } else {
          setMessage('An error occurred during email verification. Please try again.')
        }
      }
    }

    handleConfirm()
  }, [token])

  return (
    <div className="container relative h-screen flex-col items-center justify-center md:grid lg:max-w-none lg:grid-cols-2 lg:px-0">
      <div className="relative hidden h-full flex-col bg-muted p-10 text-white lg:flex dark:border-r">
        <div className="absolute inset-0 bg-slate-700" />
        <div className="relative z-20 flex items-center text-lg font-medium">
          <img src="/logo_white.png" alt="Glaium Logo" className="h-8 w-auto" />
        </div>
      </div>
      <div className="flex h-full items-center p-4 lg:p-8">
        <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
          <div className="flex flex-col space-y-2 text-center">
            <h1 className="text-2xl font-semibold tracking-tight">
              Email Verification
            </h1>
            <p className="text-sm text-muted-foreground">
              We're verifying your email address
            </p>
          </div>
          
          <Card>
            <CardContent className="pt-6">
              {state === 'pending' && (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary/20 border-t-primary mx-auto"></div>
                  <p className="text-muted-foreground mt-4">Verifying your email...</p>
                </div>
              )}


              {state === 'error' && (
                <div className="space-y-4">
                  <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800 text-center">
                    {message}
                  </div>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate('/signup')}
                  >
                    Back to Sign Up
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}